import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsFrame } from '../components/SettingsFrame';
import { teamApi } from '../api/teamApi';
import { settingsKeys } from '../queryKeys';
import { Badge } from '@/shared/ui/Badge';
import { Table, Column } from '@/shared/ui/Table';

import { useAuth } from '@/shared/hooks/useAuth';
import { TeamMember } from '../api/teamApi';
import styles from './SettingsPage.module.css';

// The "Agency" role maps to the agency_admin enum on the backend.
const AGENCY_ROLE = 'agency_admin';

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  viewer: 'Viewer',
  agency_admin: 'Agency',
};

const BASE_ROLES = ['owner', 'admin', 'manager', 'viewer'];

function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

export function TeamPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: settingsKeys.team(),
    queryFn: () => teamApi.getMembers(),
  });

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => teamApi.updateRole(id, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settingsKeys.team() }),
  });

  const members = data?.data ?? [];

  // Agency members and practice owners/admins can manage the team. Only agency
  // members may assign or change the Agency role (mirrors the backend guard).
  const isAgency = !!user?.isAgency || user?.role === AGENCY_ROLE;
  const canManage = isAgency || user?.role === 'owner' || user?.role === 'admin';
  const roleOptions = isAgency ? [...BASE_ROLES, AGENCY_ROLE] : BASE_ROLES;

  const columns: Column<TeamMember>[] = [
    {
      key: 'user',
      header: 'Member',
      render: (row) => {
        const name = [row.user.firstName, row.user.lastName].filter(Boolean).join(' ');
        return (
          <div>
            <div className={styles.teamName}>{name || row.user.email}</div>
            {name && <div className={styles.teamEmail}>{row.user.email}</div>}
          </div>
        );
      },
    },
    {
      key: 'role',
      header: 'Role',
      render: (row) => {
        // Non-agency users cannot edit an Agency member's role.
        const editable = canManage && (row.role !== AGENCY_ROLE || isAgency);
        if (!editable) {
          return <Badge variant="neutral">{roleLabel(row.role)}</Badge>;
        }
        // Keep the member's current role selectable even if it isn't in the option list.
        const options = roleOptions.includes(row.role) ? roleOptions : [...roleOptions, row.role];
        return (
          <select
            className={styles.roleSelect}
            value={row.role}
            disabled={updateRole.isPending}
            onChange={(e) => updateRole.mutate({ id: row.id, role: e.target.value })}
          >
            {options.map((role) => (
              <option key={role} value={role}>
                {roleLabel(role)}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={row.user.lastLoginAt ? 'success' : 'warning'}>
          {row.user.lastLoginAt ? 'Active' : 'Pending'}
        </Badge>
      ),
    },
  ];

  return (
    <SettingsFrame
      embedded={embedded}
      id="settings-team"
      pageClassName={styles.page}
      title={embedded ? 'PracticeNova Access' : 'Team'}
      subtitle="People who sign in to PracticeNova for this practice, and what their role lets them do."
    >
      <Table
        columns={columns}
        data={members}
        loading={isLoading}
        emptyMessage="No team members yet"
      />
    </SettingsFrame>
  );
}
